<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Textarea</h1>
    <p class="doc__lead">
      Campo de texto multilínea que comparte tokens visuales con <code>ui-input</code>.
      Soporta tres modos de redimensionado: <code>none</code>, <code>vertical</code> (manual) y
      <code>auto</code> (crece con el contenido). Incluye contador de caracteres cuando se define
      <code>maxlength</code>.
    </p>
  </header>

  <!-- ── Estados ────────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section title="Normal" description="Estado por defecto.">
      <ui-textarea
        label="Descripción"
        placeholder="Escribe aquí..."
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section title="Con valor" description="Campo con contenido prellenado.">
      <ui-textarea
        label="Notas"
        [value]="'Esto es un contenido de ejemplo en el textarea.'"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo y mensaje de error activo."
    >
      <div class="doc-example-stack">
        <ui-textarea
          label="Descripción"
          [required]="true"
          [invalid]="isInvalid()"
          message="Este campo es requerido."
          placeholder="Escribe aquí..."
        />
        <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
          {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
        </button>
      </div>
    </app-example-section>

    <app-example-section title="Deshabilitado" description="Sin interacción, fondo gris.">
      <ui-textarea
        label="Comentarios"
        [value]="'Texto deshabilitado.'"
        [disabled]="true"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section title="Solo lectura" description="Visible pero no editable.">
      <ui-textarea
        label="Registro"
        [(value)]="notes"
        [readonly]="true"
        [showOptionalHint]="false"
      />
    </app-example-section>
  </div>

  <!-- ── Tipos ─────────────────────────────────── -->
  <h2 class="doc-section-title">Variantes</h2>

  <div class="doc-grid">
    <app-example-section
      title="resize: none"
      description="Sin control de redimensionado."
    >
      <ui-textarea
        label="Sin resize"
        placeholder="Tamaño fijo..."
        resize="none"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="resize: vertical (default)"
      description="El usuario puede cambiar la altura."
    >
      <ui-textarea
        label="Redimensionable"
        placeholder="Arrastra la esquina..."
        resize="vertical"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="resize: auto"
      description="Crece automáticamente al escribir."
    >
      <ui-textarea
        label="Crecimiento automático"
        [(value)]="bio"
        placeholder="Escribe y el campo crece..."
        resize="auto"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Con contador de caracteres"
      description="showCounter + maxlength activan el contador."
    >
      <ui-textarea
        label="Descripción breve"
        [(value)]="limited"
        [maxlength]="200"
        [showCounter]="true"
        placeholder="Máximo 200 caracteres..."
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Con filas personalizadas"
      description="rows=6 aumenta la altura inicial."
    >
      <ui-textarea
        label="Observaciones"
        placeholder="Área grande..."
        [rows]="6"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="Obligatorio"
      description="Badge 'Obligatorio' y aria-required."
    >
      <ui-textarea
        label="Motivo"
        placeholder="Escribe el motivo..."
        [required]="true"
      />
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional básico:</p>
    <pre class="doc-block__pre" role="code"><code>bio = signal('');

&lt;ui-textarea
  label="Biografía"
  [(value)]="bio"
  placeholder="Cuéntanos sobre ti..."
  [required]="true"
/&gt;</code></pre>

    <p class="doc-block__p">Con contador y límite de caracteres:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-textarea
  label="Descripción"
  [(value)]="description"
  [maxlength]="500"
  [showCounter]="true"
  resize="auto"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms y validación:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-textarea
  label="Comentarios"
  [required]="true"
  [value]="form.controls.comments.value ?? ''"
  (valueChange)="form.controls.comments.setValue($event)"
  [invalid]="form.controls.comments.invalid &amp;&amp; form.controls.comments.touched"
  message="Este campo es requerido."
  [maxlength]="300"
  [showCounter]="true"
/&gt;</code></pre>
  </section>

  <!-- ── Inputs ─────────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Default</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>value</code></td><td><code>string</code> (model)</td><td><code>''</code></td><td>Valor del campo. Soporta <code>[(value)]</code>.</td></tr>
        <tr><td><code>label</code></td><td><code>string</code></td><td><code>''</code></td><td>Etiqueta visible sobre el campo.</td></tr>
        <tr><td><code>placeholder</code></td><td><code>string</code></td><td><code>''</code></td><td>Texto de ayuda cuando el campo está vacío.</td></tr>
        <tr><td><code>rows</code></td><td><code>number</code></td><td><code>3</code></td><td>Número de filas visibles iniciales.</td></tr>
        <tr><td><code>resize</code></td><td><code>'none' | 'vertical' | 'auto'</code></td><td><code>'vertical'</code></td><td>Modo de redimensionado.</td></tr>
        <tr><td><code>maxlength</code></td><td><code>number | undefined</code></td><td><code>undefined</code></td><td>Límite de caracteres. Activa contador si <code>showCounter=true</code>.</td></tr>
        <tr><td><code>showCounter</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Muestra el contador de caracteres (requiere <code>maxlength</code>).</td></tr>
        <tr><td><code>required</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Badge "Obligatorio" y <code>aria-required</code>.</td></tr>
        <tr><td><code>showOptionalHint</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Badge "Opcional".</td></tr>
        <tr><td><code>disabled</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Deshabilita el campo.</td></tr>
        <tr><td><code>readonly</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Modo solo lectura.</td></tr>
        <tr><td><code>invalid</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Borde rojo y <code>aria-invalid</code>.</td></tr>
        <tr><td><code>message</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Texto del mensaje de error.</td></tr>
        <tr><td><code>showMessage</code></td><td><code>boolean</code></td><td><code>true</code></td><td>Controla si el mensaje se renderiza.</td></tr>
        <tr><td><code>name</code></td><td><code>string | undefined</code></td><td><code>undefined</code></td><td>Atributo <code>name</code> del textarea.</td></tr>
        <tr><td><code>block</code></td><td><code>boolean</code></td><td><code>false</code></td><td>Ocupa el 100% del ancho del contenedor.</td></tr>
        <tr><td><code>fieldId</code></td><td><code>string | undefined</code></td><td>Auto-generado</td><td>ID del campo para la asociación con el label.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ───────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr><th>Nombre</th><th>Tipo</th><th>Descripción</th></tr>
      </thead>
      <tbody>
        <tr><td><code>valueChange</code></td><td><code>string</code></td><td>Emite el nuevo valor en cada keystroke.</td></tr>
        <tr><td><code>blurred</code></td><td><code>FocusEvent</code></td><td>Emite cuando el campo pierde el foco.</td></tr>
        <tr><td><code>focused</code></td><td><code>FocusEvent</code></td><td>Emite cuando el campo recibe el foco.</td></tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ──────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>Usa <code>resize="auto"</code> en formularios donde el contenido variable es largo (comentarios, notas).</li>
      <li>Usa <code>resize="none"</code> cuando necesites controlar el layout del formulario de forma estricta.</li>
      <li>Activa <code>[showCounter]="true"</code> siempre que definas un <code>maxlength</code>; da retroalimentación inmediata al usuario.</li>
      <li>Activa <code>[invalid]</code> solo tras interacción del usuario (blur o submit), no de forma anticipada.</li>
      <li>Usa <code>[showMessage]="submitted()"</code> para mostrar errores solo después de intentar enviar el formulario.</li>
      <li>Define <code>rows</code> según la cantidad de texto esperada para evitar saltos de layout al cargar el formulario.</li>
    </ul>
  </section>
</article>
